<template>
  <v-app>
    <v-container fluid class="bg-grey-lighten-4">
      <v-row justify="center">
        <v-col cols="12" lg="9" xl="8">
          <v-card elevation="4" rounded="lg">

            <!-- HEADER -->
            <v-card-title class="d-flex align-center justify-space-between bg-blue-grey-lighten-4 sticky-header">
              <span class="text-h6 font-weight-bold">New Message</span>
              <v-icon>mdi-email-edit-outline</v-icon>
            </v-card-title>

            <v-divider />

            <!-- BODY -->
            <v-card-text class="pa-4">

              <!-- PROJECT SELECTION -->
              <v-row class="mb-2">
                <v-col cols="2" class="text-grey-darken-1">Project</v-col>
                <v-col cols="10">
                  <v-autocomplete
                    v-model="selectedProject"
                    :items="projectList"
                    :item-title="(item) => `${item.code || ''} - ${item.project_title ? item.project_title.substring(0, 50) : ''}`"
                    item-value="id"
                    label="Select Project (Optional)"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    autocomplete="off"
                    clearable
                    @update:model-value="onProjectSelect"
                    return-object
                  />
                </v-col>
              </v-row>

               <!-- SEND TO ALL AUTHORS -->
              <v-row class="mb-2">
                 <v-col cols="2"></v-col>
                 <v-col cols="10">
                    <v-checkbox
                      v-model="sendToAllAuthors"
                      label="Send to All"
                      density="compact"
                      hide-details
                      color="primary"
                      @update:model-value="onAllAuthorsChange"
                  />
                 </v-col>
              </v-row>

              <!-- TO -->
              <v-row class="mb-2">
                <v-col cols="2" class="text-grey-darken-1">To</v-col>
                <v-col cols="10">
                  <v-combobox
                    v-model="to.value.value"
                    :items="emailSuggestions"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    :error-messages="to.errorMessage.value"
                    label="Recipients"
                    multiple
                    chips
                    closable-chips
                    hint="Type email and press enter"
                    persistent-hint
                    autocomplete="off"
                  />
                  <div class="text-caption text-grey mt-1">
                    {{ to.value.value.length }} recipient(s)
                  </div>
                </v-col>
              </v-row>

              <!-- CC -->
              <v-expand-transition>
                <v-row v-if="showCc" class="mb-2">
                  <v-col cols="2" class="text-grey-darken-1">Cc</v-col>
                  <v-col cols="10">
                    <v-combobox
                      v-model="cc.value.value"
                      :items="emailSuggestions"
                      density="compact"
                      variant="outlined"
                      label="Cc"
                      multiple
                      chips
                      closable-chips
                    />
                  </v-col>
                </v-row>
              </v-expand-transition>
              <!-- CC TOGGLE -->
              <v-row>
                <v-col cols="12" class="pl-2">
                  <v-btn
                    size="small"
                    variant="text"
                    @click="showCc = !showCc"
                  >
                    Add Cc
                  </v-btn>
                </v-col>
              </v-row>

              <v-divider class="my-3" />

              <!-- SUBJECT -->
              <v-text-field
                label="Subject"
                v-model="subject.value.value"
                density="compact"
                variant="outlined"
                :error-messages="subject.errorMessage.value"
                prepend-inner-icon="mdi-format-title"
                placeholder="Briefly describe your message"
                hide-details="auto"
                class="mb-4"
                autocomplete="off"
              />

              <!-- EDITOR -->
              <div class="editor-wrapper">
                <Ckeditor
                  :editor="ClassicEditor"
                  v-model="body.value.value"
                  :config="editorConfig"
                />
              </div>

              <!-- ATTACHMENTS -->
              <div class="mt-4">
                <v-btn
                  prepend-icon="mdi-paperclip"
                  variant="text"
                  @click="pickFile"
                >
                  Attach files
                </v-btn>

                <input
                  type="file"
                  ref="fileInput"
                  hidden
                  multiple
                  @change="handleFile"
                />

                <div v-if="attachments.length" class="mt-2">
                  <v-chip
                    v-for="(file, index) in attachments"
                    :key="index"
                    closable
                    class="ma-1"
                    @click:close="removeAttachment(index)"
                  >
                    {{ file.name }}
                  </v-chip>
                </div>
              </div>
            </v-card-text>

            <v-divider />

            <!-- FOOTER -->
            <v-card-actions class="pa-4 d-flex justify-space-between">
              <span class="text-caption text-grey">
                {{ attachments.length }} attachment(s)
              </span>

              <v-btn
                color="primary"
                prepend-icon="mdi-send"
                size="large"
                class="px-6"
                @click="sendMail"
              >
                Send
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>


<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import { Ckeditor } from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import * as yup from "yup";
import { ref, computed, onMounted, watch } from "vue";
import { toast } from "vue3-toastify";
import { useRouter } from "vue-router";
import { prefix } from "@/store/api";
import axios from "axios";

export default {
        components: {
            Ckeditor
        },

        setup() {
            const store = useStore();
            const router = useRouter();

            // Fetch data on mount
            onMounted(() => {
                if (store.getters.getProjectList.length === 0) {
                  store.dispatch('getProjectList');
                }
                // store.dispatch('getAuthorList');
                store.dispatch('getUsersList');
            });

            const projectList = computed(() => store.getters.getProjectList);
            // const authorList = computed(() => store.getters.getAuthorList);
            const usersList = computed(() => store.getters.getUsersList);

            const emailSuggestions = computed(() => {
                const suggestions = new Set();
                
                // Add authors
                // if (Array.isArray(authorList.value)) {
                //     authorList.value.forEach(author => {
                //       if (author.email) suggestions.add(author.email);
                //     });
                // }

                // Add users
                if (Array.isArray(usersList.value)) {
                    usersList.value.forEach(user => {
                      if (user.email) suggestions.add(user.email);
                    });
                }
                
                // Add project customers/contacts if available
                if (Array.isArray(projectList.value)) {
                  projectList.value.forEach(proj => {
                        if (proj.sales_orders && proj.sales_orders.email) {
                          suggestions.add(proj.sales_orders.email);
                        }
                     });
                }

                return Array.from(suggestions);
            });

            const { handleSubmit, resetForm } = useForm({
                validationSchema: yup.object({
                    to: yup.array().of(yup.string().email()).min(1, "At least one recipient is required").label("To"),
                    cc: yup.array().of(yup.string().email()).nullable().label("CC"),
                    subject: yup.string().required().label("Subject"),
                    body: yup.string().nullable().label("Body")
                }),
            });

            const to = useField("to");
            const cc = useField("cc");
            const subject = useField("subject");
            const body = useField("body");
            const showCc = ref(false);
            const showBcc = ref(false);
            const attachments = ref([]);
            const errorMessages = ref("");
            const loading = ref("");
            const selectedProject = ref(null);
            const sendToAllAuthors = ref(false);

            const fileInput = ref(null);

            // Initialize to/cc as arrays
            to.value.value = [];
            cc.value.value = [];

            const pickFile = () => {
                fileInput.value.click();
            };

            const handleFile = (event) => {
              const file = event.target.files[0];
              if (file) attachments.value.push(file);
            };

            const removeAttachment = (index) => {
              attachments.value.splice(index, 1);
            };

            const onProjectSelect = (project) => {
              if (Array.isArray(project.sales_orders)) {
                to.value.value = [];
                project.sales_orders.forEach(item => {
                    if (item.email && !to.value.value.includes(item.email)) {
                      to.value.value.push(item.email);
                    }
                });
              }
            };

            const onAllAuthorsChange = (isChecked) => {
                if (isChecked) {

                    // if (Array.isArray(authorList.value)) {
                    //     authorList.value.forEach(author => {
                    //       if (author.email && !to.value.value.includes(author.email)) {
                    //         to.value.value.push(author.email);
                    //       }
                    //     });
                    // }

                    if (Array.isArray(usersList.value)) {
                      usersList.value.forEach(user => {
                        if (user.email && !to.value.value.includes(user.email)) {
                          to.value.value.push(user.email);
                        }
                      });
                    }

                    if (Array.isArray(projectList.value)) {
                      projectList.value.forEach(proj => {
                        if (Array.isArray(proj.sales_orders)) {
                          proj.sales_orders.forEach(item => {
                            if (item.email && !to.value.value.includes(item.email)) {
                              to.value.value.push(item.email);
                            }
                          });
                        }
                      });
                    }
                } else {
                    // Optional: remove authors if unchecked? 
                    // Better to leave them or clear all? 
                    // Usually "Send to All" implies adding. Unchecking might not mean removing specific ones.
                    // But for user convenience, let's leave them.

                    to.value.value = [];
                }
            };

            const sendMail = handleSubmit((values) => {
                const formData = new FormData();
                
                // Handle 'to' and 'cc' arrays -> send as arrays
                if (Array.isArray(values.to)) {
                    values.to.forEach(email => {
                        formData.append('to[]', email);
                    });
                }
                
                if (Array.isArray(values.cc)) {
                    values.cc.forEach(email => {
                        formData.append('cc[]', email);
                    });
                }

                formData.append('subject', values.subject);
                if (values.body) formData.append('body', values.body);

                if (selectedProject.value) {
                    formData.append('project_id', selectedProject.value.id);
                }

                attachments.value.forEach((file, index) => {
                    formData.append(`attachments[${index}]`, file);
                });

                store.commit('SET_LOADING', { color: 'primary' });
                axios.post(prefix + "cms/send_mail", formData).then((response) => {
                    resetForm();
                    // Reset custom fields
                    to.value.value = [];
                    cc.value.value = [];
                    selectedProject.value = null;
                    sendToAllAuthors.value = false;
                    attachments.value = [];
                    
                    store.commit('UNSET_LOADING');
                    toast.success(response.data.message, {
                        timeout: 3000,
                    });
                })
                .catch((error) => {
                    store.commit('UNSET_LOADING');
                    console.error("Error:", error);
                    toast.error(error.response?.data?.message || "An error occurred");
                });

            });

            return {
                to,
                cc,
                subject,
                body,
                showCc,
                showBcc,
                attachments,
                fileInput,
                ClassicEditor,
                editorConfig: {
                    toolbar: {
                    items: [
                        "heading", "|",
                        "bold", "italic", "underline", "|",
                        "link", "bulletedList", "numberedList", "|",
                        "outdent", "indent", "|",
                        "blockQuote", "|",
                        "undo", "redo",
                    ],
                    },
                    language: "en",
                },
                pickFile,
                handleFile,
                removeAttachment,
                sendMail,
                projectList,
                selectedProject,
                sendToAllAuthors,
                emailSuggestions,
                onProjectSelect,
                onAllAuthorsChange
            }
        }
    }
</script>

<style>
    .visually-hidden-label .v-field-label {
        display: none !important;
    }
    .ck-editor__editable {
        min-height: 150px !important;
        height: 200px !important;
    }
</style>